import styled, { css } from 'styled-components';
import { Button } from '@m/alchemy-ui/Button';
import { color, typeStyle, units } from '@m/alchemy-ui/theme';

export const LoginSection = styled.section`
  margin-bottom: ${units(2)};
`;

export const LoginHeader = styled.header`
  display: flex;
  align-items: center;
  margin-bottom: ${units(3)};
`;

export const LoginImage = styled.div`
  img {
    max-width: 300px;
    max-height: 60px;
    object-fit: contain;
  }
`;

export const LoginTitle = styled.h2`
  font-weight: 700;
  font-size: 20px;
  margin: 0;
  padding: 0;

  ${LoginImage} + & {
    margin-left: ${units(2)};
  }
`;

export const AllLoginTypeLayout = styled.div`
  display: flex;
  flex-wrap: wrap;

  & > * {
    width: 100%;
    margin: ${units(2)};
  }

  @media (min-width: 800px) {
    & > * {
      width: calc(50% - ${units(4)});
    }
  }
`;

export const TabSelector = styled.div`
  width: auto;
  margin: 0;
`;

const tabItemBase = css`
  display: flex;
  align-items: center;
  position: relative;
  padding: ${units(2)};
  border: 1px solid #65788b;
  border-bottom: 0;
  transition: background 0.3s ease-in-out;
  background: #ffffff;
  width: 100%;
  cursor: pointer;

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-bottom: 1px solid #65788b;
    border-radius: 0 0 8px 8px;
  }

  &:hover {
    background: #f2f2f2;
  }
`;

export const TabItem = styled.button<{ $active?: boolean }>`
  ${tabItemBase};

  ${({ $active }) =>
    $active &&
    css`
      background: #f2f2f2;

      &::before,
      &::after {
        position: absolute;
        right: -12px;
        top: 50%;
        transform: translateY(-50%);
        content: '';
        width: 0;
        height: 0;
      }

      &::before {
        border-top: 13px solid transparent;
        border-bottom: 13px solid transparent;
        border-left: 13px solid #65788b;
        right: -13px;
      }

      &::after {
        border-top: 12px solid transparent;
        border-bottom: 12px solid transparent;
        border-left: 12px solid #f2f2f2;
      }
    `}
`;

export const TabIcon = styled.span`
  display: inline-block;
  width: 24px;
  height: 24px;
  vertical-align: middle;

  svg {
    display: block;
    max-width: 100%;
    max-height: 100%;
    color: ${color('typeHeader')};
  }
`;

export const TabLabel = styled.span`
  margin-left: ${units(2)};
  color: ${color('typeHeader')};
  font-weight: 700;
`;

export const MobileViewSelector = styled.select`
  width: 100%;
  padding: 7px 12px;
  border: 1px solid #8c93ab;
  border-radius: 16px;
  background: #f8f9fa
    url('data:image/svg+xml; charset=utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20height%3D%2224%22%20width%3D%2224%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cpath%20fill%3D%22none%22%20stroke%3D%22%239B9B9B%22%20stroke-linecap%3D%22round%22%20d%3D%22M3%206l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')
    no-repeat calc(100% - 6px) center / 20px;
  box-shadow:
    0 1px 1px 0px rgba(0, 0, 0, 0.02),
    0 2px 3px 0px rgba(0, 0, 0, 0.05);
  appearance: none;
  cursor: pointer;
`;

export const SignInPanel = styled.div`
  width: 100%;

  @media (min-width: 800px) {
    max-width: 358px;
  }
`;

export const PanelTitle = styled.h3`
  margin-bottom: ${units(1)};
`;

export const PanelDescription = styled.p`
  margin-bottom: ${units(2)};
  font-weight: 400;
`;

export const PanelButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${units(2)} 0;
  border-top: 1px solid ${color('line')};
`;

export const PanelButton = styled(Button)``;

export const PanelLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${units(1)} ${units(2)};
  border-radius: 16px;
  text-decoration: none;
  cursor: pointer;
  background: ${color('base', { palette: 'interactiveConstructive' })};
  color: ${color('contrast', { palette: 'interactiveConstructive' })};
  border: none;
  transition: background 0.3s ease-in-out;

  &:hover {
    opacity: 0.9;
  }
`;

export const LoginLink = styled.a`
  padding: ${units(1)} ${units(2)};
  line-height: 1.5;
  cursor: pointer;
  border-radius: 18px;
  background: ${color('base', { palette: 'interactiveConstructive' })};
  color: ${color('container', { palette: 'interactiveConstructive' })};
  font-weight: 700;
  border: none;
  transition: background 0.3s ease-in-out;

  &:hover {
    opacity: 0.9;
  }
`;

export const SsoErrorAlert = styled.div.attrs({ className: 'mdl-alert -error' })``;

export const SwitchCommunityContainer = styled.div`
  margin-bottom: ${units(2)};
`;

export const SwitchCommunityButton = styled(Button)``;

export const FormsContainer = styled.div``;

export const RegistrationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  padding: ${units(2)} 0;
  border-top: 1px solid ${color('line')};
  margin-top: ${units(1.25)};
`;

export const RegistrationText = styled.p`
  ${typeStyle('bodyS')};
  margin-right: auto;
`;

export const RegisterButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${units(1)} ${units(2)};
  ${typeStyle('labelM')};
  border-radius: 18px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  background: ${color('container')};
  color: ${color('base', { palette: 'interactiveConstructive' })};
  border: 1px solid ${color('base', { palette: 'interactiveConstructive' })};
  transition: background 0.3s ease-in-out;

  &:focus,
  &:hover,
  &:active {
    text-decoration: none;
    background: ${color('highlight', { palette: 'interactiveConstructive' })};
  }
`;

export const SoloLoginContainer = styled.div`
  width: 100%;
`;

export const SoloHeading = styled.p`
  ${typeStyle('subHeaderM')};
  margin-bottom: ${units(2)};
`;
